import { useCallback, useMemo, useRef, useState } from 'react';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { useQueryClient } from 'react-query';
import { DraftV1 } from '@frontend/api-sms-draft';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { useAlert } from '@frontend/design-system';

// Prefix used to purge all cached variants of the smart draft query when a draft is dismissed.
const SMART_DRAFT_QUERY_KEY_PREFIX = ['DraftV1', 'GenerateSmartDraft'];

type UseSmartDraftProps = {
  smartDraftMessages: SMS[];
  threadId?: string;
  aiBotStatusActive?: boolean;
};

export function useSmartDraft({ smartDraftMessages, threadId, aiBotStatusActive }: UseSmartDraftProps) {
  // Tracks which thread the user dismissed (insert or X).
  const [dismissedForThreadId, setDismissedForThreadId] = useState<string | null>(null);
  // Tracks the previous thread so we can reset dismissal when the thread changes.
  const [prevResolvedThreadId, setPrevResolvedThreadId] = useState<string | undefined>(undefined);
  const stableMessagesRef = useRef<{ ids: string; messages: SMS[] }>({ ids: '', messages: [] });
  const queryClient = useQueryClient();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const alert = useAlert();
  const { t } = useTranslation();

  const hasSmartDraftsEnabled = getFeatureFlagValue('inbox-smart-drafts');
  const resolvedThreadId = threadId || smartDraftMessages[0]?.threadId;

  // Render-phase reset: when the thread changes, clear the dismissal so returning to a
  // previously-dismissed thread always triggers a fresh fetch (no useEffect needed).
  if (prevResolvedThreadId !== resolvedThreadId) {
    setPrevResolvedThreadId(resolvedThreadId);
    setDismissedForThreadId(null);
  }

  // dismissed is true only while actively on the thread where the user dismissed.
  const dismissed = dismissedForThreadId === resolvedThreadId && dismissedForThreadId !== null;

  const { stableMessages, serializedMessageData, hasInbound } = useMemo(() => {
    if (!smartDraftMessages?.length) {
      return { stableMessages: [], serializedMessageData: '', hasInbound: false };
    }

    const currentIds = smartDraftMessages
      .map((msg) => `${msg.id}-${msg.locationId}`)
      .sort()
      .join('|');
    const { ids: previousIds, messages: previousMessages } = stableMessagesRef.current;

    let finalMessages: SMS[];
    if (currentIds !== previousIds) {
      stableMessagesRef.current = { ids: currentIds, messages: smartDraftMessages };
      finalMessages = smartDraftMessages;
    } else {
      finalMessages = previousMessages.length ? previousMessages : smartDraftMessages;
    }

    return {
      stableMessages: finalMessages,
      serializedMessageData: currentIds,
      hasInbound: finalMessages.some((sms) => sms.direction === Direction.DIRECTION_INBOUND),
    };
  }, [smartDraftMessages]);

  const shouldShowSmartDraft = hasSmartDraftsEnabled && !!serializedMessageData && !aiBotStatusActive && hasInbound;

  const { data, isFetching, refetch } = DraftV1.Queries.useGenerateSmartDraftQuery({
    request: { messages: stableMessages },
    options: {
      // Auto-starts on thread open. Disabled only when dismissed in this session.
      // Clearing the RQ cache (removeQueries) on dismiss ensures a fresh fetch on next enable.
      enabled: shouldShowSmartDraft && !dismissed,
      onSuccess: ({ suggestedMessage }) => {
        if (!suggestedMessage) {
          alert.error(t('Error generating smart draft'));
        }
      },
      onError: (error: any) => {
        alert.error(t('Error generating smart draft'));
        sentry.error({
          error,
          topic: 'messages',
          team: 'fe-messaging',
          severityLevel: 'error',
        });
      },
    },
  });

  // Purges React Query's internal cache for this query so the next enable triggers a fresh fetch
  // (not a stale-data cache hit). Called on insert, close, and redo.
  const clearQueryCache = useCallback(() => {
    queryClient.removeQueries(SMART_DRAFT_QUERY_KEY_PREFIX);
  }, [queryClient]);

  // Insert accepted: text inserted externally by caller, dismiss and wipe cache so reopen
  // always generates a fresh draft rather than restoring the accepted text.
  const acceptSmartDraft = useCallback(() => {
    setDismissedForThreadId(resolvedThreadId ?? null);
    clearQueryCache();
  }, [resolvedThreadId, clearQueryCache]);

  // X / close: discard preview, dismiss and wipe cache so reopen generates fresh.
  const closeSmartDraft = useCallback(() => {
    setDismissedForThreadId(resolvedThreadId ?? null);
    clearQueryCache();
  }, [resolvedThreadId, clearQueryCache]);

  // refetch() forces a network request even if RQ somehow retained stale data.
  const generateSmartDraft = useCallback(() => {
    setDismissedForThreadId(null);
    refetch();
  }, [refetch]);

  // Redo: wipe cache and force a fresh fetch without changing dismissed state.
  const redoSmartDraft = useCallback(() => {
    clearQueryCache();
    refetch();
  }, [clearQueryCache, refetch]);

  const smartDraft = data?.suggestedMessage ?? '';

  // smart draft button (borderless) when dismissed. smart draft button (bordered) when eligible but idle (no result, not loading).
  const showSmartDraftButton = shouldShowSmartDraft && (dismissed || (!isFetching && !smartDraft));
  const isLoadingSmartDraft = shouldShowSmartDraft && !dismissed && isFetching;
  const showSmartDraftResult = shouldShowSmartDraft && !dismissed && !!smartDraft && !isFetching;

  return {
    smartDraft,
    showSmartDraftButton,
    showSmartDraftButtonNoBorder: dismissed,
    isLoadingSmartDraft,
    showSmartDraftResult,
    generateSmartDraft,
    redoSmartDraft,
    acceptSmartDraft,
    closeSmartDraft,
  };
}
